<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { ref } from 'vue'
import { useLocalStorage, refDebounced } from '@vueuse/core'
import AgentDetailsPanel from '@/components/BusinessLogic/VirtualAgent/AgentDetailsPanel.vue'
import PhoneAssignmentPanel from '@/components/BusinessLogic/Panel/PhoneAssignmentPanel.vue'
import type { Filters } from '@/types/Filters'
import DeleteConfigurationModal from '@/components/BusinessLogic/Modal/DeleteConfigurationModal.vue'
import UnassignPhoneModal from '@/components/BusinessLogic/Modal/UnassignPhoneModal.vue'
import LiveTestModal from '@/components/BusinessLogic/Modal/LiveTestModal.vue'
import { useNativeWebComponentEvents } from '@/composables/webcomponents/useNativeWebComponentEvents'
import { WebComponentTags } from '@/enums/WebComponents'
import { useFilters } from '@/composables/filters/useFilters'
import type { Configurator } from 'shared/src'
import { useLanguages } from '@/composables/translations/useLanguages'
import { useKnowledgeApi } from '@/composables/api/useKnowledgeApi'
import { useConfigurations } from '@/composables/Configurations/useConfigurations.ts'
import { STORAGE_KEYS } from '@/enums/StorageEnum'
import { usePhoneFormatter } from '@/composables/formatting/usePhoneFormatter'
import { useConfiguratorActions } from '@/composables/configurator/useConfiguratorActions'
import { useConfiguratorApi } from '@/composables/api/useConfiguratorApi'
import { useQueryClient } from '@tanstack/vue-query'
import { configurationDataQueryKey } from '@/composables/queries/configurations/useConfigurationDataQuery'
import { useConfiguratorTable } from '@/composables/configurator/useConfiguratorTable'
import { useDateTime } from '@ai/ui-components'
import { useConfigurationsListQuery } from '@/composables/queries/configurations/useConfigurationsListQuery'
import { useAvailableLanguagesQuery } from '@/composables/queries/configurations/useAvailableLanguagesQuery'
import { useDeleteConfigurationMutation } from '@/composables/queries/configurations/useDeleteConfigurationMutation'
import { useToggleEnabledMutation } from '@/composables/queries/configurations/useToggleEnabledMutation'
import { ActionButton } from '@/components/BusinessLogic/TableActions'
import { useVariablesAllQuery } from '@/composables/queries/variables'
import VariableDescription from '@/components/BusinessLogic/Variables/VariableDescription.vue'
import type { Action } from '@ai/ui-components'

const { data: variablesData } = useVariablesAllQuery()
const variableNames = computed(() => new Set((variablesData.value ?? []).map((v) => v.name)))
const { formatDateTime } = useDateTime()

interface Props {
  locale: string
  filters?: string | Filters
}

interface BaseTableProps {
  item: Configurator
  index: number
}

const props = defineProps<Props>()

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const nativeWebComponent = document.querySelector<HTMLElement>(
  WebComponentTags.AiListConfigurators,
)!
const { t, locale: i18nLocale } = useI18n({ useScope: 'global' })

const phonePanel = ref<{ open: () => Promise<void> } | null>(null)
const globalActions = computed<Action[]>(() => [
  {
    label: t('assign_phone_number'),
    value: 'assign_phone_number',
    handler: () => {
      void phonePanel.value?.open()
    },
    danger: false,
  },
])

const { emitCustomEvent } = useNativeWebComponentEvents(nativeWebComponent)
const { mutateAsync: deleteConfigMutation } = useDeleteConfigurationMutation()
const { mutateAsync: updateEnabled } = useToggleEnabledMutation()

// Wrapper to match useConfiguratorActions expected signature
const deleteConfiguration = async (id: string, element?: HTMLElement) => {
  await deleteConfigMutation({ id })
  if (element) {
    const { emitCustomEvent: emit } = useNativeWebComponentEvents(element)
    emit('configuration-deleted', { id })
  }
}

onBeforeMount(() => {
  i18nLocale.value = props.locale.replace(/[^a-z]/g, '')
})

const searchTerm = ref('')
const searchTermDebounced = refDebounced(searchTerm, 600)
const FILTERS_VERSION = 'v2'
const storedLanguages = useLocalStorage<string[]>(STORAGE_KEYS.LIST_CONFIGURATORS_FILTERS, [])
const storedFiltersVersion = useLocalStorage<string>(
  STORAGE_KEYS.LIST_CONFIGURATORS_FILTERS_VERSION,
  '',
)
const { selectedConfiguratorColumns, selectedLanguages } = useFilters(
  props.filters ?? '',
  (updated) => {
    emitCustomEvent('filtersChanged', { updated })
  },
)

// Initialize from localStorage synchronously (before query runs)
if (storedFiltersVersion.value !== FILTERS_VERSION) {
  storedLanguages.value = []
  storedFiltersVersion.value = FILTERS_VERSION
} else if (storedLanguages.value.length) {
  selectedLanguages.value = storedLanguages.value
}

const { exportConfiguration } = useConfigurations()

// Pagination state
const page = ref(1)
const limit = useLocalStorage<number>(STORAGE_KEYS.VIRTUAL_AGENTS_PAGE_SIZE, 20)

// Reset page immediately when search/filters change (before debounce)
watch([searchTerm, selectedLanguages], () => {
  page.value = 1
  storedLanguages.value = selectedLanguages.value ?? []
})

// TanStack Query for configurations list
const queryParams = computed(() => ({
  q: searchTermDebounced.value || undefined,
  language: selectedLanguages.value?.length ? selectedLanguages.value.join(',') : undefined,
  page: page.value,
  limit: limit.value,
  locale: props.locale,
}))

const { data: configurationsData, isLoading, refetch } = useConfigurationsListQuery(queryParams)

const listConfigsPaginated = ref<Configurator[]>([])
watch(
  () => configurationsData.value?.configs,
  (configs) => {
    listConfigsPaginated.value = configs ?? []
  },
  { immediate: true },
)
const pagination = computed(
  () =>
    configurationsData.value?.pagination ?? {
      has_more: false,
      limit: 20,
      page: 1,
      processing_time_ms: 0,
      total_hits: 0,
      total_pages: 0,
    },
)

// TanStack Query for available languages
const { data: availableLanguages } = useAvailableLanguagesQuery()

// Table composable
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- exposed for tests
const { allColumns, allColumnKeys, columnsOptions, tableState, tableStateModel, normalizeKeys } =
  useConfiguratorTable(selectedConfiguratorColumns)

const hasSelectedConfiguratorColumns = computed(
  () => selectedConfiguratorColumns.value?.some((column) => column !== 'name') ?? false,
)

onMounted(() => {
  if (!selectedConfiguratorColumns.value?.length) {
    selectedConfiguratorColumns.value = ['name']
  }
})

const emit = defineEmits<(e: 'onCreate') => void>()

const onCreate = () => {
  emitCustomEvent('onCreate', {})
  emit('onCreate')
}

const editTo = (id: string) => `/${props.locale}/configurator/${id}`
const duplicateTo = (id: string) => ({
  path: editTo(id),
  query: { duplicate: 'true' },
})

// Agent details panel state
const selectedForDetails = ref<Configurator | undefined>()
const openDetailsPanel = ref(false)
const loadingDetailsId = ref<string | null>(null)

const { getConfigurationData } = useConfiguratorApi()
const { fetchDocumentsSummary } = useKnowledgeApi()
const queryClient = useQueryClient()

const openAgentDetails = async (item: Configurator) => {
  if (loadingDetailsId.value === item.id) return
  loadingDetailsId.value = item.id
  try {
    await Promise.all([
      queryClient.prefetchQuery({
        queryKey: configurationDataQueryKey(item.id),
        queryFn: () => getConfigurationData(item.id),
      }),
      fetchDocumentsSummary(),
    ])
  } finally {
    loadingDetailsId.value = null
    selectedForDetails.value = item
    openDetailsPanel.value = true
  }
}

// Actions composable
const {
  selectedForDelete,
  openDeleteModal,
  selectedForUnassign,
  openUnassignModal,
  onUnassignPhones,
  selectedForLiveTest,
  openLiveTestModal,
  getActions,
  selectedKeys,
  openBulkDeleteModal,
  deleteModalCount,
  onConfirmDelete: _onConfirmDelete,
  onBulkDuplicateConfigurations,
} = useConfiguratorActions({
  editTo,
  duplicateTo,
  exportConfiguration: (...args) => {
    void exportConfiguration(...args)
  },
  deleteConfiguration: async (...args) => {
    await deleteConfiguration(...args)
  },
  listConfigsPaginated,
  nativeWebComponent,
  onViewDetails: (item: Configurator) => {
    void openAgentDetails(item)
  },
})

const isBulkOperationPending = ref(false)

const runBulkOp = async <T,>(op: () => Promise<T>): Promise<T> => {
  isBulkOperationPending.value = true
  const result = await op()
  page.value = 1
  setTimeout(() => refetch().finally(() => (isBulkOperationPending.value = false)), 1500)
  return result
}

const onConfirmDelete = () => runBulkOp(_onConfirmDelete)
const onBulkDuplicate = () => runBulkOp(onBulkDuplicateConfigurations)

const getBulkSelectionSummary = (selectedCount: number) =>
  selectedCount === 1
    ? t('one_virtual_agent_selected')
    : t('n_virtual_agents_selected', [selectedCount])

const { languageLabel } = useLanguages()

const uiLocale = computed(() => (i18nLocale.value.startsWith('fr') ? 'fr' : 'en'))

const getLanguageLabel = (item: Configurator) =>
  languageLabel(uiLocale.value, item.voice_locale ?? 'en-US')

const getFallbackFlag = (item: Configurator) => {
  const parts = item.voice_locale?.split('-')
  return (parts?.[1] ?? parts?.[0])?.toLowerCase() ?? 'empty'
}

const languageOptions = computed(() => {
  const langs = availableLanguages.value ?? []
  const baseLangCount = new Map<string, number>()
  for (const { code } of langs) {
    const base = code.split('-')[0].toLowerCase()
    baseLangCount.set(base, (baseLangCount.get(base) ?? 0) + 1)
  }
  const seen = new Map<string, { label: string; value: string; flag: string }>()
  for (const { code, flag } of langs) {
    const baseLang = code.split('-')[0].toLowerCase()
    const baseLabel = languageLabel(uiLocale.value, baseLang)
    let label = baseLabel
    if ((baseLangCount.get(baseLang) ?? 0) > 1) {
      const fullLabel = languageLabel(uiLocale.value, code)
      const stripped = fullLabel.replace(baseLabel, '').trim()
      if (stripped && !stripped.startsWith('(')) label = stripped
    }
    const existing = seen.get(code)
    // Prefer country-specific flags (e.g. 'us') but upgrade from bare lang (e.g. 'en' → 'us')
    if (!existing || existing.flag === baseLang) seen.set(code, { label, value: code, flag })
  }
  return [...seen.values()]
})

const onUpdateEnabled = async (item: Configurator) => {
  try {
    await updateEnabled({ id: item.id, enabled: !item.enabled })
    // toast.success(t('item_enabled_status_updated', [item.name]))
  } catch (error) {
    console.error('Failed to update enabled status:', error)
    // Revert to previous state by toggling back
    item.enabled = !item.enabled
    // toast.error(t('failed_to_update_enabled_status', [item.name]))
  }
}

const paginationBinding = computed(() => ({
  currentPage: page.value,
  itemsPerPage: limit.value,
  totalItems: pagination.value.total_hits ?? 0,
  totalPages: pagination.value.total_pages ?? 0,
}))

const { formatPhone } = usePhoneFormatter()

const formatPhones = (phones: string | null) => {
  if (!phones) return '-'
  return phones
    .split(', ')
    .map((phone) => formatPhone(phone))
    .join(', ')
}

const MAX_VISIBLE_FLAGS = 3

const getAllLanguages = (item: Configurator) => {
  const primary = { flag: item.voice_flag ?? getFallbackFlag(item), label: getLanguageLabel(item) }
  const secondary = (item.secondary_languages ?? []).map((l) => ({ flag: l.flag, label: l.label }))
  return [primary, ...secondary]
}

const getVisibleLanguages = (item: Configurator) =>
  getAllLanguages(item).slice(0, MAX_VISIBLE_FLAGS)
const getHiddenLanguages = (item: Configurator) => getAllLanguages(item).slice(MAX_VISIBLE_FLAGS)
const getHiddenTooltip = (item: Configurator) =>
  getHiddenLanguages(item)
    .map((l) => l.label)
    .join('\n')
</script>

<template>
  <OTable
    id="configurators"
    dataTestid="configurators-list"
    v-model="listConfigsPaginated"
    v-model:selectedKeys="selectedKeys"
    :columns="allColumns.value"
    with-pagination
    with-bulk-actions
    row-key="id"
    sortable
    :loading="isLoading || isBulkOperationPending"
    resizable
    column-resize-mode="indicator"
    :pagination="paginationBinding"
    v-model:currentPage="page"
    v-model:itemsPerPage="limit"
    :state-persistence-key="STORAGE_KEYS.TABLE_STATE_BUILD_VIRTUAL_AGENTS"
    v-model:tableState="tableStateModel"
  >
    <template #description="{ item }">
      <VariableDescription
        :text="(item as Configurator).description"
        :variable-names="variableNames"
      />
    </template>

    <template #created_at="{ item }">
      <OOverflowTooltip
        :content="formatDateTime({ format: 'dateTime', value: item.created_at, fallback: '-' })"
      >
        {{ formatDateTime({ format: 'dateTime', value: item.created_at, fallback: '-' }) }}
      </OOverflowTooltip>
    </template>
    <template #last_modification="{ item }">
      <OOverflowTooltip
        :content="
          formatDateTime({ format: 'dateTime', value: item.last_modification, fallback: '-' })
        "
      >
        {{ formatDateTime({ format: 'dateTime', value: item.last_modification, fallback: '-' }) }}
      </OOverflowTooltip>
    </template>
    <template #filterActions>
      <div class="flex justify-between items-center w-full">
        <div class="flex space-x-3 items-center">
          <OInput
            v-model="searchTerm"
            :placeholder="t('search')"
            size="S"
            dataTestid="configurators-search"
            class="w-[264px]"
          >
            <template #icon>
              <UiIcon name="search" />
            </template>
          </OInput>
          <ODropdown
            v-model="selectedLanguages"
            :options="languageOptions"
            medium
            :searchable="languageOptions.length >= 6"
            multi-select
            :filter-title="t('language')"
            filter
            isLocaleDropdown
            class="!w-fit"
            dataTestid="configurators-languages"
          />
        </div>
        <div class="flex items-center gap-3 w-full justify-end">
          <ODropdown
            v-model="selectedConfiguratorColumns"
            :options="columnsOptions"
            multi-select
            filter
            class="!w-auto"
          >
            <template #icon>
              <UiIcon
                :color="hasSelectedConfiguratorColumns ? 'text-primary' : 'text-color-1'"
                name="column"
                variant="outline"
              />
            </template>
          </ODropdown>
          <OButton
            size="M"
            :label="t('create')"
            @click="onCreate"
            dataTestid="configurator-create"
          />
          <OListActions
            :actions="globalActions"
            placement="bottom-end"
            data-testid="configurators-global-actions"
            small
          />
        </div>
      </div>
    </template>

    <template #enabled="{ item }: BaseTableProps">
      <OSwitch v-model="item.enabled" @click="onUpdateEnabled(item)" />
    </template>
    <template #phone="{ item }: BaseTableProps">
      <OOverflowTooltip :content="formatPhones(item.phone)">
        {{ formatPhones(item.phone) }}
      </OOverflowTooltip>
    </template>

    <template #language="{ item }: BaseTableProps">
      <span class="flex items-center gap-3 [&_img]:max-h-[1em] [&_img]:w-auto">
        <OToolTip
          v-for="lang in getVisibleLanguages(item as Configurator)"
          :key="lang.flag"
          :content="lang.label"
          placement="top"
          class="inline-flex items-center"
        >
          <span style="display: inline-flex; clip-path: inset(0 round 2px)">
            <UiIcon :name="lang.flag" class="!w-4 !h-3" />
          </span>
        </OToolTip>
        <OToolTip
          v-if="getHiddenLanguages(item as Configurator).length"
          :content="getHiddenTooltip(item as Configurator)"
          placement="top"
          class="inline-flex items-center"
        >
          <span class="typography-body-2 text-neutral-2 cursor-default">
            +{{ getHiddenLanguages(item as Configurator).length }}
          </span>
        </OToolTip>
      </span>
    </template>

    <template #bulk-selection-summary="{ selectedCount }">
      <span
        class="typography-body-2-strong text-neutral-1"
        data-testid="configurations-bulk-selection-summary"
      >
        {{ getBulkSelectionSummary(selectedCount) }}
      </span>
    </template>

    <template #bulk-actions="{ selectedCount }">
      <div class="flex items-center gap-2">
        <OButton
          :label="t('duplicate')"
          variant="tertiary"
          size="S"
          :disabled="selectedCount === 0"
          data-testid="configurations-bulk-duplicate"
          @click="onBulkDuplicate"
        />
        <OButton
          :label="t('delete')"
          variant="tertiary"
          color="error"
          size="S"
          :disabled="selectedCount === 0"
          data-testid="configurations-bulk-delete"
          @click="openBulkDeleteModal"
        />
      </div>
    </template>

    <template #actions="{ item }">
      <div class="flex items-center space-x-2">
        <ActionButton
          :tooltip="t('view')"
          icon="preview"
          icon-data-test="view-agent-details"
          :loading="loadingDetailsId === (item as Configurator).id"
          @click="openAgentDetails(item as Configurator)"
          data-testid="row-action-view"
        />
        <ActionButton
          :tooltip="t('edit')"
          icon="edit"
          icon-data-test="edit-pen"
          :to="editTo(item.id)"
          data-testid="row-action-edit"
        />
        <OListActions
          data-testid="row-action-more"
          :shadow-host-selector="WebComponentTags.AiListConfigurators"
          small
          :actions="getActions(item)"
        />
      </div>
    </template>
  </OTable>

  <PhoneAssignmentPanel ref="phonePanel" @assigned="refetch" />

  <DeleteConfigurationModal
    v-if="openDeleteModal"
    v-model:open="openDeleteModal"
    :item="(selectedForDelete as Configurator)?.name"
    :count="deleteModalCount"
    :secondary-action-promise="onConfirmDelete"
    data-testid="row-delete-modal"
  />

  <UnassignPhoneModal
    v-if="openUnassignModal"
    v-model:open="openUnassignModal"
    :config-name="(selectedForUnassign as Configurator)?.name"
    :phones="(selectedForUnassign as Configurator)?.phone"
    data-testid="row-unassign-modal"
    @unassign="
      async (phones) => {
        if (await onUnassignPhones(selectedForUnassign as Configurator, phones))
          queryClient.invalidateQueries({
            queryKey: ['configurations', 'list'],
            refetchType: 'active',
          })
      }
    "
  />

  <LiveTestModal
    v-model:open="openLiveTestModal"
    :config-id="(selectedForLiveTest as Configurator)?.id"
    :config-name="(selectedForLiveTest as Configurator)?.name"
    data-testid="row-live-test-modal"
  />

  <AgentDetailsPanel
    v-if="selectedForDetails"
    v-model:open="openDetailsPanel"
    :configurator="selectedForDetails"
    :locale="locale"
    data-testid="agent-details-panel"
  />
</template>

<style scoped>
:deep([data-testid='otable-bulk-actions-row']) {
  background-color: var(--color-grayscale-100) !important;
  border-radius: var(--size-radius-m) !important;
}

:deep([data-testid='otable-bulk-actions-spacer']) {
  padding-top: calc(var(--spacing) * 4);
}
</style>
